import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'

import { ProductKey } from '@context/ProductContext'

import { TokenStorage } from '@utils/tokenStorage'

import { LeadDto } from '@model/LeadDto'
import { clearNotificationHiding } from '@model/Notification'

import { TickmillAppBannerClosedKey } from '@global/TickmillAppBanner/TickmillAppBanner'

/**
 * SAFE STORAGE
 * provides a safe wrapper around localStorage/sessionStorage that handles SSR, web workers
 */

type StorageType = 'local' | 'session'

class InMemoryStorage implements Storage {
  private store: Map<string, string> = new Map()

  get length(): number {
    return this.store.size
  }

  clear(): void {
    this.store.clear()
  }

  getItem(key: string): string | null {
    return this.store.get(key) ?? null
  }

  key(index: number): string | null {
    const keys = Array.from(this.store.keys())
    return keys[index] ?? null
  }

  removeItem(key: string): void {
    this.store.delete(key)
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value)
  }
}

class SafeStorageHandler {
  private memoryStorage = new InMemoryStorage()
  private isLocalStorageAvailable = false
  private isSessionStorageAvailable = false

  constructor() {
    this.isLocalStorageAvailable = this.checkStorageAvailability('localStorage')
    this.isSessionStorageAvailable = this.checkStorageAvailability('sessionStorage')

    if (!this.isLocalStorageAvailable) {
      console.warn('[SafeStorage] localStorage is not available. Using in-memory fallback.')
    }
    if (!this.isSessionStorageAvailable) {
      console.warn('[SafeStorage] sessionStorage is not available. Using in-memory fallback.')
    }
  }

  private checkStorageAvailability(storageType: 'localStorage' | 'sessionStorage'): boolean {
    try {
      if (typeof window === 'undefined') {
        return false
      }

      const storage = window[storageType]
      if (!storage) {
        return false
      }

      const testKey = '__storage_test__'
      storage.setItem(testKey, 'test')
      storage.removeItem(testKey)
      return true
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      // storage is unavailable (privacy mode, quota exceeded, etc.)
      return false
    }
  }

  private getStorage(type: StorageType): Storage {
    if (type === 'local') {
      return this.isLocalStorageAvailable ? window.localStorage : this.memoryStorage
    }
    return this.isSessionStorageAvailable ? window.sessionStorage : this.memoryStorage
  }

  getItem(key: string, type: StorageType = 'local'): string | null {
    try {
      const storage = this.getStorage(type)
      return storage.getItem(key)
    } catch (e) {
      console.error(`[SafeStorage] Error getting item "${key}":`, e)
      return null
    }
  }

  setItem(key: string, value: string, type: StorageType = 'local'): boolean {
    try {
      const storage = this.getStorage(type)
      storage.setItem(key, value)
      return true
    } catch (e) {
      console.error(`[SafeStorage] Error setting item "${key}":`, e)
      // If quota exceeded, try to clear some space
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.warn('[SafeStorage] Storage quota exceeded')
      }
      return false
    }
  }

  removeItem(key: string, type: StorageType = 'local'): void {
    try {
      const storage = this.getStorage(type)
      storage.removeItem(key)
    } catch (e) {
      console.error(`[SafeStorage] Error removing item "${key}":`, e)
    }
  }

  clear(type: StorageType = 'local'): void {
    try {
      const storage = this.getStorage(type)
      storage.clear()
    } catch (e) {
      console.error('[SafeStorage] Error clearing storage:', e)
    }
  }

  /**
   * get and parse JSON value from storage
   */
  getJSON<T>(key: string, type: StorageType = 'local'): T | null {
    try {
      const raw = this.getItem(key, type)
      if (!raw) {
        return null
      }
      return JSON.parse(raw) as T
    } catch (e) {
      console.error(`[SafeStorage] Error parsing JSON for "${key}":`, e)
      return null
    }
  }

  /**
   * stringify and set JSON value to storage
   */
  setJSON<T>(key: string, value: T, type: StorageType = 'local'): boolean {
    try {
      const serialized = JSON.stringify(value)
      return this.setItem(key, serialized, type)
    } catch (e) {
      console.error(`[SafeStorage] Error stringifying value for "${key}":`, e)
      return false
    }
  }

  /**
   * check if storage is available
   */
  isAvailable(type: StorageType = 'local'): boolean {
    return type === 'local' ? this.isLocalStorageAvailable : this.isSessionStorageAvailable
  }
}

const safeStorage = new SafeStorageHandler()

// exports for localStorage
export const safeLocalStorage = {
  getItem: (key: string) => safeStorage.getItem(key, 'local'),
  setItem: (key: string, value: string) => safeStorage.setItem(key, value, 'local'),
  removeItem: (key: string) => safeStorage.removeItem(key, 'local'),
  clear: () => safeStorage.clear('local'),
  getJSON: <T>(key: string) => safeStorage.getJSON<T>(key, 'local'),
  setJSON: <T>(key: string, value: T) => safeStorage.setJSON(key, value, 'local'),
  isAvailable: () => safeStorage.isAvailable('local'),
}

// exports for sessionStorage
export const safeSessionStorage = {
  getItem: (key: string) => safeStorage.getItem(key, 'session'),
  setItem: (key: string, value: string) => safeStorage.setItem(key, value, 'session'),
  removeItem: (key: string) => safeStorage.removeItem(key, 'session'),
  clear: () => safeStorage.clear('session'),
  getJSON: <T>(key: string) => safeStorage.getJSON<T>(key, 'session'),
  setJSON: <T>(key: string, value: T) => safeStorage.setJSON(key, value, 'session'),
  isAvailable: () => safeStorage.isAvailable('session'),
}

// HOOKS
function usePersistedState<T>(
  storageType: 'local' | 'session',
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const storage = storageType === 'local' ? safeLocalStorage : safeSessionStorage

  const [value, setValue] = useState<T>(() => {
    const parsed = storage.getJSON<T>(key)
    return parsed ?? defaultValue
  })

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key) {
        setTimeout(() => {
          if (e.newValue) {
            setValue(JSON.parse(e.newValue))
          } else {
            setValue(defaultValue)
          }
        }, 0)
      }
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [key, defaultValue])

  const setter = useCallback(
    (newValue: SetStateAction<T>) => {
      setValue((existingValue) => {
        const valueToSet =
          typeof newValue === 'function' ? (newValue as (p: T) => T)(existingValue) : newValue

        if (valueToSet !== undefined && valueToSet !== null) {
          storage.setJSON(key, valueToSet)
        } else {
          storage.removeItem(key)
        }

        // ONLY dispatch storage event if we're in a browser environment
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new StorageEvent('storage', {
              key,
              oldValue: JSON.stringify(existingValue),
              newValue: JSON.stringify(valueToSet),
            })
          )
        }

        return valueToSet
      })
    },
    [storage, key]
  )

  return [value, setter]
}

export function useLocallyPersistedState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  return usePersistedState('local', key, defaultValue)
}

export function useSessionPersistedState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  return usePersistedState('session', key, defaultValue)
}

// UTILS
export const clearLocalStorage = (): void => {
  safeLocalStorage.removeItem('hideDocumentExpiration')
  safeLocalStorage.removeItem('auth')
  safeLocalStorage.removeItem('IntroducingBrokerContext')
  safeLocalStorage.removeItem('luckyDrawEligible')
  safeLocalStorage.removeItem('luckyDrawViewed')
  safeLocalStorage.removeItem('account')
  safeLocalStorage.removeItem('isBrowserOutdatedModalDisplayed')
  safeLocalStorage.removeItem('productHandled')
  safeLocalStorage.removeItem('walletId')
  safeLocalStorage.removeItem(ProductKey)
  safeLocalStorage.removeItem('hasAdditionalId')
  safeLocalStorage.removeItem('hubSpotIdentificationToken')
  safeLocalStorage.removeItem('statusBannerOpen')
  safeLocalStorage.removeItem('hasUserAnyWallets')
  safeLocalStorage.removeItem('selectedProductIdsKey')
  safeLocalStorage.removeItem('hasCartItems')
  safeLocalStorage.removeItem('cartLink')
  safeLocalStorage.removeItem('selectedProductIdsKey')
  safeLocalStorage.removeItem(TickmillAppBannerClosedKey)
  safeLocalStorage.removeItem('lead')
  safeLocalStorage.removeItem('ibSignupFormSubmitted')
  safeLocalStorage.removeItem('pagination')
  safeLocalStorage.removeItem('sessionWarning')
  safeLocalStorage.removeItem('lastActivityTimestamp')
  safeLocalStorage.removeItem('sessionTimer')
  TokenStorage.clear()
  clearNotificationHiding()
}

export const getLeadFromStorage = (): LeadDto | null => {
  return safeLocalStorage.getJSON<LeadDto>('lead')
}
